import produce, { Draft } from 'immer';
import { getType } from 'typesafe-actions';

import { SHOW_BANNER_IMAGE_CANVAS, ShowBannerImageCanvas } from '../actions/banners-edit/banner-image';
import {
    OPEN_CREATIVE_CONSTRUCTOR,
    OPEN_CREATIVE_CONSTRUCTOR_PRESETS,
    OPEN_CREATIVE_HTML5_ADD_CONSTRUCTOR,
    OPEN_CREATIVE_HTML5_CONSTRUCTOR,
    OPEN_CREATIVE_IDEAS,
    OpenCreativeConstructor,
    OpenCreativeConstructorPresets,
    OpenCreativeHtml5AddConstructor,
    OpenCreativeHtml5Constructor,
    OpenCreativeIdeas
} from '../actions/creative';
import {
    OPEN_OVERLAY_CONSTRUCTOR,
    OPEN_VIDEO_CONSTRUCTOR,
    OPEN_VIDEO_CONSTRUCTOR_WITH_CPC_VIDEO,
    OpenOverlayConstructor,
    OpenVideoConstructor,
    OpenVideoConstructorWithCpcVideo
} from '../actions/video';
import { itemNameEditors } from '../canvas/CanvasEditableLabel/reducers';
import { CanvasEditorAction, changeCreativeType } from '../canvas/common/actions';
import { getPresetId, isCreateImmediately } from '../canvas/common/helpers';
import { canvasReducer, getCanvasInitialState } from '../canvas/common/reducers';
import { initialSharedData, Page } from '../canvas/common/typings';
import { getInitialHtml5AppState, html5Reducer } from '../canvas/Html5App/reducers';
import { Html5Page } from '../canvas/Html5App/typings';
import { CanvasEditorState } from '../typings/state';

const canvasEditorInitialState: CanvasEditorState = {
    sharedData: initialSharedData,
    html5: getInitialHtml5AppState(),
    itemNameEditors: {},
    canvas: getCanvasInitialState()
};

export const canvasEditorReducer = (
    state: CanvasEditorState = canvasEditorInitialState,
    action:
        | CanvasEditorAction
        | OpenCreativeHtml5Constructor
        | OpenCreativeHtml5AddConstructor
        | OpenVideoConstructor
        | OpenVideoConstructorWithCpcVideo
        | OpenCreativeConstructor
        | OpenCreativeConstructorPresets
        | OpenCreativeIdeas
        | ShowBannerImageCanvas
        | OpenOverlayConstructor
): CanvasEditorState => {
    return produce(state, (draft: Draft<CanvasEditorState>) => {
        switch (action.type) {
            case OPEN_CREATIVE_HTML5_CONSTRUCTOR:
            case OPEN_CREATIVE_HTML5_ADD_CONSTRUCTOR:
                const page = action.type === OPEN_CREATIVE_HTML5_CONSTRUCTOR ? Html5Page.ACTIVE : Html5Page.UPLOAD;

                draft.itemNameEditors = {};
                draft.sharedData = action.sharedData;
                draft.html5 = {
                    ...getInitialHtml5AppState(action.sharedData),
                    page
                };

                return;

            case OPEN_OVERLAY_CONSTRUCTOR:
            case OPEN_VIDEO_CONSTRUCTOR:
            case OPEN_VIDEO_CONSTRUCTOR_WITH_CPC_VIDEO:
            case OPEN_CREATIVE_CONSTRUCTOR:
            case OPEN_CREATIVE_CONSTRUCTOR_PRESETS:
            case OPEN_CREATIVE_IDEAS:
            case SHOW_BANNER_IMAGE_CANVAS:
                draft.sharedData = action.sharedData;
                draft.itemNameEditors = {};
                draft.canvas = getCanvasInitialState(action.sharedData);
                draft.canvas.commonData.page = action.page;

                if (action.page === Page.PRESETS && isCreateImmediately(action.sharedData)) {
                    draft.canvas.commonData.page = Page.CONSTRUCTOR;
                    draft.canvas.addCreative.presetId = getPresetId(action.sharedData);
                    draft.canvas.addCreative.returnPage = Page.CREATIVES;
                }

                if (action.page === Page.CONSTRUCTOR) {
                    draft.canvas.addCreative.presetId = getPresetId(action.sharedData);
                    draft.canvas.addCreative.creativeId = action.sharedData.creativeId;
                    draft.canvas.addCreative.returnPage = Page.CREATIVES;
                }

                return;

            case getType(changeCreativeType):
                draft.sharedData.creativeType = action.creativeType;

                return;

            default:
                draft.canvas = canvasReducer(draft.canvas, action);
                draft.html5 = html5Reducer(draft.html5, action);
                draft.itemNameEditors = itemNameEditors(state.itemNameEditors, action);

                return;
        }
    });
};
